import React, { useState, useRef, useEffect, useCallback } from "react";
import styles from "./VehicleHeaderBar.module.css";
import { useVehicleShopUrls } from "../../hooks/useVehicleShopUrls";

export interface ActiveVehicle {
  id: string;
  description: string;          // "2024 Toyota Camry LE"
  year: number;
  make: string;
  model: string;
  variant?: string;
  tireSize?: string;             // null if vehicle identified but size not yet known
  staggered?: boolean;
  frontSize?: string;
  rearSize?: string;
  runflat?: boolean;
  shopUrl?: string;              // Server-built tirerack.com search URL; the frontend never builds one (TRAC-905)
  source: "ymmv" | "vehicle_info_card" | "tdg_link_card" | "tire_size_confirmation" | "session_restore" | "pdp_context";
  timestamp: number;
}

interface VehicleHeaderBarProps {
  activeVehicle: ActiveVehicle;
  vehicleHistory: ActiveVehicle[];
  onSwitchVehicle: (vehicle: ActiveVehicle) => void;
  // TRAC-896: `origin` is the control that was pressed, so the vehicle picker
  // can grow out of it. Both of these live in the header bar / its dropdown.
  onChooseNewVehicle: (origin?: HTMLElement | null) => void;
  onSelectTires: (vehicle: ActiveVehicle, origin?: HTMLElement | null) => void;
}

// TRAC-918: makes whose name is more than one word (every one in the fitment data, 2026-08-17
// scan). A one-token make split turned "2026 Aston Martin DBX S" into make="Aston",
// model="Martin", which duplicated the header vehicle and broke its Shop Tires link.
export const MULTI_WORD_MAKES = [
  "Alfa Romeo",
  "American Motors",
  "Aston Martin",
  "Campagna Motors",
  "De Tomaso",
  "Facel Vega",
  "Land Rover",
  "Lordstown Motors",
  "Mobility Ventures",
  "SSC North America",
];

/**
 * Parse a vehicle description string like "2024 Toyota Camry LE" into parts.
 * `knownMakes` (e.g. the makes already in the header's vehicle history) are matched before
 * the one-token fallback, longest first, alongside MULTI_WORD_MAKES.
 */
export function parseVehicleDescription(desc: string, knownMakes: string[] = []): {
  year: number;
  make: string;
  model: string;
  variant?: string;
} | null {
  const trimmed = desc.trim().replace(/\s+/g, " ");
  const yearMatch = trimmed.match(/^(\d{4}) (.+)$/);
  if (yearMatch) {
    const rest = yearMatch[2];
    const makes = [...knownMakes, ...MULTI_WORD_MAKES]
      .map((m) => m.trim().replace(/\s+/g, " "))
      .filter((m) => m.includes(" "))
      .sort((a, b) => b.length - a.length);
    // A known make with nothing after it has no model: not a vehicle.
    if (makes.some((m) => rest.toLowerCase() === m.toLowerCase())) return null;
    const make = makes.find((m) => rest.toLowerCase().startsWith(`${m.toLowerCase()} `));
    if (make) {
      const modelMatch = rest.slice(make.length + 1).match(/^(\S+)(?: (.+))?$/);
      if (!modelMatch) return null;
      return {
        year: parseInt(yearMatch[1], 10),
        make: rest.slice(0, make.length),
        model: modelMatch[1],
        variant: modelMatch[2] || undefined,
      };
    }
  }
  // Match: year (4 digits) + make (word) + model (word) + optional variant (rest)
  const match = trimmed.match(/^(\d{4})\s+(\S+)\s+(\S+)(?:\s+(.+))?$/);
  if (!match) return null;
  return {
    year: parseInt(match[1], 10),
    make: match[2],
    model: match[3],
    variant: match[4] || undefined,
  };
}

// TRAC-319: Pure tire-size variant names (e.g. "LT285/70-17") are redundant
// with the tire size already shown in the header bar — skip them in display.
const PURE_SIZE_VARIANT = /^[A-Z]{0,2}\d+\/\d+-\d+$/;

// TRAC-299: Tire-size variant detection (display only; links are server-built, TRAC-905).
// Matches single sizes ("255/60-20", "LT285/70-17") and stagger descriptions
// ("225/55-19F 245/50-19R", "225/55-19F 245/50-19R Runflat").
// Mirrors tdg_tools.py:_is_tire_size_variant().
const STAGGER_COMPONENT = /\d+\/\d+-\d+[FR]/g;
function isTireSizeVariant(variant: string): boolean {
  if (PURE_SIZE_VARIANT.test(variant)) return true;
  const stripped = variant.replace(/\s*runflat\s*$/i, "").trim();
  if (STAGGER_COMPONENT.test(stripped)) {
    STAGGER_COMPONENT.lastIndex = 0; // reset global regex
    const remaining = stripped.replace(STAGGER_COMPONENT, "").trim();
    STAGGER_COMPONENT.lastIndex = 0;
    if (!remaining) return true;
  }
  STAGGER_COMPONENT.lastIndex = 0;
  return false;
}

/**
 * Compact vehicle name: "Year Model Variant" (omits make to preserve
 * the most useful info when the row truncates).
 */
function compactVehicleName(vehicle: ActiveVehicle): string {
  const parts = [String(vehicle.year), vehicle.model];
  if (vehicle.variant && !isTireSizeVariant(vehicle.variant)) {
    // TRAC-375: Skip appending variant if model already ends with it
    // (e.g. agent returns model="Camry LE" and YMMV sets variant="LE")
    const modelLower = vehicle.model.toLowerCase();
    const variantLower = vehicle.variant.toLowerCase();
    if (modelLower !== variantLower && !modelLower.endsWith(` ${variantLower}`)) {
      parts.push(vehicle.variant);
    }
  }
  return parts.join(" ");
}

const VehicleHeaderBar: React.FC<VehicleHeaderBarProps> = ({
  activeVehicle,
  vehicleHistory,
  onSwitchVehicle,
  onChooseNewVehicle,
  onSelectTires,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on click outside
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  const handleDropdownToggle = useCallback(() => {
    setDropdownOpen((prev) => !prev);
  }, []);

  const handleSwitchVehicle = useCallback((vehicle: ActiveVehicle) => {
    onSwitchVehicle(vehicle);
    setDropdownOpen(false);
  }, [onSwitchVehicle]);

  const handleChooseNew = useCallback((e?: React.MouseEvent<HTMLElement>) => {
    onChooseNewVehicle(e?.currentTarget ?? null);
    setDropdownOpen(false);
  }, [onChooseNewVehicle]);

  const handleSelectTires = useCallback((vehicle: ActiveVehicle, e?: React.MouseEvent<HTMLElement>) => {
    e?.stopPropagation();
    onSelectTires(vehicle, e?.currentTarget ?? null);
    setDropdownOpen(false);
  }, [onSelectTires]);

  const shopUrlFor = useVehicleShopUrls([activeVehicle, ...vehicleHistory]);
  const shopUrl = shopUrlFor(activeVehicle);
  const hasTireSize = !!(activeVehicle.tireSize || activeVehicle.frontSize);
  const otherVehicles = vehicleHistory.filter((v) => v.id !== activeVehicle.id);
  const hasOtherVehicles = otherVehicles.length > 0;

  // Format tire size display
  let tireSizeDisplay: string | null;
  if (activeVehicle.frontSize && activeVehicle.rearSize) {
    tireSizeDisplay = `F: ${activeVehicle.frontSize}\u00A0\u00A0\u00A0R: ${activeVehicle.rearSize}`;
  } else if (activeVehicle.tireSize) {
    tireSizeDisplay = activeVehicle.tireSize;
  } else {
    tireSizeDisplay = null; // no size — "Select Tires" button shown instead
  }

  return (
    // data-ymmv-persistent-chrome: this bar is the one thing above the vehicle
    // picker that stays on screen while the picker is open, so TRAC-896's morph
    // has to stop short of it instead of covering it and handing it back at the
    // reveal. Marker only — see `interfaceBox` in utils/ymmvMorph.ts.
    <div className={styles.vehicleHeaderBar} ref={dropdownRef} data-ymmv-persistent-chrome="">
      <div className={styles.vehicleInfo} onClick={handleDropdownToggle} style={{ cursor: "pointer" }}>
        <svg
          className={`${styles.chevron} ${dropdownOpen ? styles.chevronOpen : ""}`}
          width="21"
          height="17"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="9 6 15 12 9 18" />
        </svg>

        <div className={styles.vehicleDetails}>
          <span className={styles.vehicleDescription}>{compactVehicleName(activeVehicle)}</span>
          {tireSizeDisplay && <span className={styles.tireSizeDisplay}>{tireSizeDisplay}</span>}
        </div>
      </div>

      <div className={styles.vehicleActions}>
        {hasTireSize ? (
          shopUrl && (
            <a
              className={styles.shopLink}
              href={shopUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-track-id="vehicle_header_shop_tires"
              data-track-turn={-1}
              data-track-option={compactVehicleName(activeVehicle)}
              data-track-extra={JSON.stringify({
                url: shopUrl,
                vehicleId: activeVehicle.id,
              })}
            >
              Shop Tires
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
            </a>
          )
        ) : (
          <button
            className={styles.selectTiresBtn}
            onClick={(e) => handleSelectTires(activeVehicle, e)}
            title="Add tire size for better recommendations"
            data-track-id="vehicle_header_select_tires"
            data-track-turn={-1}
            data-track-option={compactVehicleName(activeVehicle)}
            data-track-extra={JSON.stringify({ vehicleId: activeVehicle.id })}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="16" />
              <line x1="8" y1="12" x2="16" y2="12" />
            </svg>
            Add Tire Size
          </button>
        )}
      </div>

      {/* Dropdown — always rendered (at minimum contains "Choose a new vehicle") */}
      <div className={`${styles.dropdown} ${dropdownOpen ? styles.dropdownOpen : ""}`}>
        {otherVehicles.length > 0 && (
          <div
            className={styles.dropdownVehicleList}
            data-track-id="vehicle_header_switch_vehicle"
            data-track-turn={-1}
          >
            {otherVehicles.map((vehicle) => {
              const vehicleShopUrl = shopUrlFor(vehicle);
              return (
                <button
                  key={vehicle.id}
                  className={styles.dropdownItem}
                  onClick={() => handleSwitchVehicle(vehicle)}
                  data-track-option={compactVehicleName(vehicle)}
                  data-track-extra={JSON.stringify({ vehicleId: vehicle.id })}
                >
                  <span className={styles.dropdownVehicleName}>{compactVehicleName(vehicle)}</span>
                  {(vehicle.tireSize || vehicle.frontSize) ? (
                    <>
                      <span className={styles.dropdownTireSize}>
                        {vehicle.frontSize && vehicle.rearSize
                          ? `F: ${vehicle.frontSize}\u00A0\u00A0\u00A0R: ${vehicle.rearSize}`
                          : vehicle.tireSize}
                      </span>
                      {vehicleShopUrl && (
                        <a
                          className={styles.dropdownShopLink}
                          href={vehicleShopUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          data-track-id="vehicle_header_shop_tires"
                          data-track-turn={-1}
                          data-track-option={compactVehicleName(vehicle)}
                          data-track-extra={JSON.stringify({
                            url: vehicleShopUrl,
                            vehicleId: vehicle.id,
                          })}
                        >
                          Shop Tires
                        </a>
                      )}
                    </>
                  ) : (
                    <button
                      className={styles.dropdownSelectTiresBtn}
                      onClick={(e) => handleSelectTires(vehicle, e)}
                      title="Add tire size for better recommendations"
                      data-track-id="vehicle_header_add_size"
                      data-track-turn={-1}
                      data-track-option={compactVehicleName(vehicle)}
                      data-track-extra={JSON.stringify({ vehicleId: vehicle.id })}
                    >
                      + Size
                    </button>
                  )}
                </button>
              );
            })}
          </div>
        )}
        {otherVehicles.length > 0 && <div className={styles.dropdownDivider} />}
        <button
          className={styles.dropdownItem}
          onClick={handleChooseNew}
          data-track-id="vehicle_header_new_vehicle"
          data-track-turn={-1}
          data-track-option="choose_new_vehicle"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <span>Choose a new vehicle</span>
        </button>
      </div>
    </div>
  );
};

export default VehicleHeaderBar;
